import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthContext = createContext({
  user: null,
  loading: true,
  signIn: () => {},
  signOut: async () => {},
  refresh: async () => {},
  showWall: false,
  wallReason: "audio",
  requireAuth: () => {},
  showPaywall: () => {},
  dismissWall: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWall, setShowWall] = useState(false);
  const [wallReason, setWallReason] = useState("audio");

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me", { withCredentials: true });
      setUser(r.data);
    } catch { setUser(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signIn = useCallback(() => {
    // Redirect user to Emergent OAuth and return to current page.
    const redirect = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const signOut = useCallback(async () => {
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch {}
    setUser(null);
  }, []);

  const requireAuth = useCallback((reason = "audio") => {
    if (user) return true;
    setWallReason(reason);
    setShowWall(true);
    return false;
  }, [user]);

  // Unconditionally open the paywall — used for tier upgrade prompts on
  // locked premium/professional articles where the user IS signed in but
  // needs to pick a plan. VexGlobalLinks is always free and MUST NOT call this.
  const showPaywall = useCallback((reason = "premium") => {
    setWallReason(reason);
    setShowWall(true);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh: checkAuth, showWall, wallReason, requireAuth, showPaywall, dismissWall: () => setShowWall(false) }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Component that intercepts the #session_id=... URL fragment after OAuth redirect,
// exchanges it for a persistent session cookie, then clears the URL.
export const AuthCallback = ({ onDone }) => {
  const hasProcessed = React.useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { onDone && onDone(null); return; }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id }, { withCredentials: true });
        // Clean URL and hand user data back to caller
        window.history.replaceState({}, document.title, window.location.pathname);
        onDone && onDone(r.data);
      } catch (e) {
        window.history.replaceState({}, document.title, window.location.pathname);
        onDone && onDone(null);
      }
    })();
  }, [onDone]);
  return null;
};
